import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Select } from "antd";
import { AimOutlined, GroupOutlined, MenuOutlined, SaveOutlined, ExpandOutlined } from "@ant-design/icons";
import { HubConnectionBuilder, HttpTransportType } from "@microsoft/signalr";
import { useDispatch } from "react-redux";
import "../assets/libs/fontawesome-5.2.0/css/all.css";
import "../assets/styles/index.scss";
import { Canvas } from "../components/canvas";
import service from "../service";
import {
  setSpinning,
  setSelectedSeats,
  setChannelList,
  setShowZoomWatermark,
  setSelectedChannel
} from "../redux/designerPropertiesSlice";
import ChannelSelector from "../components/channels/ChannelSelector";
import ChannelManagement from "../components/channels/ChannelManagement";
import { useCanvasZoom } from "../hooks/useCanvasZoom";
import { useSeatsVisibility } from "../hooks/useSeatsVisibility";
import { applyAutoFit, useAutoFitOnContainerResize } from "../hooks/useAutoFitOnContainerResize";
import { useSeatHubConnection } from "../hooks/useSeatHubConnection";
import { getSvgObject } from "../utils/ObjectUtil";
import { Icons } from "../enums";
import { fabric } from "fabric";

const MapChannel = () => {
  const dispatch = useDispatch();
  const canvasRef = useRef();
  const mapContainerRef = useRef();
  const [connection, setConnection] = useState(null);
  const [selectionType, setSelectionType] = useState("individual");
  const [showManagement, setShowManagement] = useState(false);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const { canZoomIn, canZoomOut } = useCanvasZoom(canvasRef);

  const handleManageClick = () => {
    setShowManagement(true);
  };

  const handleCloseManagement = () => {
    setShowManagement(false);
  };

  useEffect(() => {
    dispatch(setSpinning(true));
    initScript();
    return () => {
      dispatch(setShowZoomWatermark(false));
    };
  }, []);

  const handleHubChangeStatus = useCallback((value) => {
    canvasRef.current.handler.channelHandler.setType("seat");
    canvasRef.current.handler.channelHandler.handleObjectLockUpdate(value);
  }, []);

  useSeatHubConnection(connection, handleHubChangeStatus);

  useSeatsVisibility(canvasRef, canZoomIn, isMapLoaded, (seat) => {
    if (seat.isSelected) return;// Si el asiento no tiene canal o está seleccionado, no hace nada

    // Mostrar/ocultar iconos y labels
    const seatCircle = seat.item(0);
    const baseColor = seat.channel.key !== "sn" ? seat.channel.color : seat.statusColor.Free;
    const fill = new fabric.Color(baseColor).setAlpha(0.25).toRgba();
    const stroke = baseColor;

    seatCircle.set({ fill: fill, stroke: stroke });

    // si el asiento es accesible, de compañia o tiene vista restringida, mostrar icono
    if (seat.accesible || seat.companion || seat.restrictedView) {
      if (seat.restrictedView)
        seat.insertAt(getSvgObject(Icons.restricted, (seatCircle.radius * 1.7), '#000000'), 2, true);
      else if (seat.accesible)
        seat.insertAt(getSvgObject(Icons.accesible, (seatCircle.radius * 1.7), '#000000'), 2, true);
      else if (seat.companion)
        seat.insertAt(getSvgObject(Icons.companion, (seatCircle.radius * 1.7), '#000000'), 2, true);
    } else {
      const seatText = seat.item(1);
      seatText.set({
        visible: true
      });
    }
  }, (seat) => {
    if (seat.isSelected) return;// Si el asiento no tiene canal o está seleccionado, no hace nada

    // Restaurar propiedades customizadas
    const seatCircle = seat.item(0);
    seatCircle.set({ fill: seat.status == "Free" ? `${seat.statusColor.Free}80` : seat.statusColor.Free, stroke: "transparent" });

    const seatText = seat.item(1);
    seatText.set({ visible: false });

    const seatIcon = seat.item(2);
    if (seatIcon) {
      seat.remove(seatIcon);
    }
  });

  useAutoFitOnContainerResize(mapContainerRef, canvasRef, isMapLoaded, {
    alignTop: true,
  });

  const initScript = async () => {
    window.redseats.setSettigs(process.env.REACT_APP_ENVIROMENT, "Channel");
    if (window.redseats.publicKey) await init();
    else window.redseats.component.callback = init;
  };

  const init = async () => {
    const mapEvent = await service.mapEvent.buildChannel();
    if (mapEvent.key) {
      await setChannels();
      // dispatch(setSelectedSeats(mapEvent.selectedSeats));
      const mapCanvas = JSON.parse(mapEvent.mapCanvas);
      await handlers.onLoad(mapCanvas);

      //signalr
      const newConnection = new HubConnectionBuilder()
        .withUrl(`${process.env.REACT_APP_HUB_URL}object`, {
          transport: HttpTransportType.WebSockets | HttpTransportType.LongPolling,
          withCredentials: true,
        })
        .withAutomaticReconnect()
        .build();

      setConnection(newConnection);
    }
  };

  const handlers = {
    onLoad: async ({ Objects }) => {
      if (Objects) {
        canvasRef.current.handler.clear(true);
        const data = Objects.filter((obj) => {
          return obj.id;
        });
        await canvasRef.current.handler.importJSON(data);
      }

      canvasRef.current.handler.interactionMode = "channel";
      canvasRef.current.handler.enablePanning = true;
      dispatch(setSpinning(false));
      setIsMapLoaded(true);
    },
  };

  const handleSaveChanges = () => {
    canvasRef.current.handler.channelHandler.handleSaveChanges();
  };

  const handleChangeSelect = (value) => {
    canvasRef.current.handler.enablePanning =
      value == "individual" ? true : false;
    canvasRef.current.handler.isMultipleSelectionEnabled = value == "group";
    canvasRef.current.handler.canvas.skipTargetFind = value == "group";
    setSelectionType(value);
  };

  const saveChannels = async (channelsToSave) => {
    await service.channel.saveChannels(channelsToSave);
    await setChannels();
    await init();
    dispatch(setSelectedChannel({ name: "", color: "", selected: false, key: "" }));
    setShowManagement(false);
  };

  const setChannels = async () => {
    let channels = [
      { name: "Sin canal", color: "#000000", selected: false, key: "sn" },
    ];
    const response = await service.channel.getChannels();
    if (response) {
      response.forEach((channel) => {
        channel.selected = false;
        channel.action = "None";
      });
      channels = channels.concat(response);
    }
    dispatch(setChannelList(channels));
  };

  const handleZoomToFit = () => {
    applyAutoFit(canvasRef);
  }

  return (
    <div className="map">
      <article className="map-canvas-container" ref={mapContainerRef}>
        <div className="map-header-toolbar">
          <Button
            onClick={() => {
              canvasRef.current.handler.zoomHandler.zoomOut();
            }}
            disabled={!canZoomOut}
          >
            <span className="icon-zoom-out"></span>
          </Button>

          <Button
            onClick={handleZoomToFit}
          >
            <ExpandOutlined />
          </Button>
          <Button
            onClick={() => {
              canvasRef.current.handler.zoomHandler.zoomIn();
            }}
            disabled={!canZoomIn}
          >
            <span className="icon-icon-zoom-in"></span>
          </Button>
          <Select
            style={{ width: 140 }}
            onSelect={handleChangeSelect}
            defaultValue="individual"
          >
            <Select.Option value="individual"><AimOutlined /> Individual</Select.Option>
            <Select.Option value="group"><GroupOutlined /> Grupo</Select.Option>
          </Select>
          <Button
            onClick={() => {
              handleSaveChanges()
            }}
          >
            <SaveOutlined /> <span className="label">Guardar</span>
          </Button>
        </div>

        <div className="channel-selector-panel">
          <ChannelSelector
            onManageClick={handleManageClick}
          />
          {showManagement && (
            <ChannelManagement
              onClose={handleCloseManagement}
              saveChannels={saveChannels}
            />
          )}
        </div>

        <div
          className="canvas-wrapper"
          onMouseEnter={() => dispatch(setShowZoomWatermark(true))}
          onMouseLeave={() => dispatch(setShowZoomWatermark(false))}
        >
          <Canvas
            ref={canvasRef}
            editable={false}
            canvasOption={{ backgroundColor: "#FFFFFF", defaultCursor: "default" }}
            maxZoom={1000}
            minZoom={100}
            onZoom={() => dispatch(setShowZoomWatermark(false))}
            gridOption={{ enabled: false }}
          />
        </div>
      </article>
    </div>
  );
};

export default MapChannel;
